import {useCallback, useMemo, useRef, useState} from 'react'
import {Stack} from '@src/CommonComponents/Stack/Stack.tsx'
import styles from "./checkInPage.module.less"
import {useEventsStore} from "@src/Store/eventsStore.ts";
import {
    CheckInOutPair,
    EmployeeEvents, EmployeeInline,
    WorkEventKindEnum as KindEnum, WorkdayWithEmployee,
    WorkEventCreateInputRequest, WorkEventIngestOutput, WorkEventOutput
} from "@src/httpClient/__generated__/Api.ts";
import {Header} from "@src/Features/MainLayout/Components/Header/Header.tsx";
import {CheckInOutSkeleton} from "@src/Pages/Views/Manager/CheckInOut/skeleton/CheckInOutSkeleton.tsx";
import {StatusBanner} from "@src/CommonComponents/StatusBanner/StatusBanner.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {
    Body_16_Coral_Emphasis_16,
    Title_20_Coral_Strong_emphasis_20,
    Title_40_Coral_emphasis_40
} from "@UI/typography/generated/typography.tsx";
import cn from "classnames";
import {getCurrentDateTimeIso} from "@src/utils/formatDate.ts";
import {Input, InputHandlers} from "@src/CommonComponents/Input/InputNative.tsx";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {t} from "i18next";
import {Search} from "@UI/components/icons/Search.tsx";
import {apiClient} from "@src/httpClient/api.ts";
import {addEventOffline, addEventToOutbox} from "@src/offline/offline.ts";
import {useNavigate, useSearchParams} from "react-router-dom";
import {LogOut} from "@UI/components/icons/LogOut.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {GeoState, useGpsStore} from "@src/Store/gpsStore.ts";
import {useMutation} from "@tanstack/react-query";
import {AxiosError, AxiosResponse} from "axios";
import {GPSSuccessModal} from "@src/Modals/GPSSuccessModal.tsx";
import {GPSWaitModal} from "@src/Modals/GPSWaitModal.tsx";
import {v4 as uuid} from "uuid";
import {GPSFailModal} from "@src/Modals/GPSFailModal.tsx";
import {useErrorStore} from "@src/Layers/ErrorLayer/ErrorStore.ts";
import {useModalStore} from "@src/Layers/ModalLayer/ModalStore.ts";
import {useWorkDaysStore} from "@src/Store/workDaysStore.ts";
import {LogIn} from "@UI/components/icons/LogIn.tsx";
import {AskFinishWork} from "@src/Modals/AskModal.tsx";
import {useModalState} from "@src/Store/modalStore.ts";
import {EmployeeStatus, WorkStatus} from "@src/CommonComponents/WorkStatus/WorkStatus.tsx";
import {parseEmployeeStatus} from "@src/utils/workStatusParser.ts";
import {DataNotFoundMessage} from "@src/Pages/Views/Common.tsx";
import {Clock} from "@src/CommonComponents/Clock/Clock.tsx";
import {Modal} from "@src/CommonComponents/Modal/Modal.tsx";
import {sleep} from "@src/offline/sync.ts";
import {EventsHistoryGroup} from "@src/CommonComponents/EventsHistoryElement/EventsHistoryGroup";
import {WorkDay} from "@src/db.ts";
import {ClearResultsButton} from "@src/CommonComponents/ClearResultsButton/ClearResultsButton.tsx";
import {EmployeeWithWorkdays} from "@src/Store/employeesStore.ts";
import {GpsStatusButton} from "@src/CommonComponents/GpsStatusButton/GpsStatusButton.tsx";
import {FilterTile, FilterTiles} from "@src/CommonComponents/FilterTiles/FilterTiles.tsx";


const isEmployeeEventsValid = (groups: CheckInOutPair[]): boolean => {
    if (groups.length === 0) return false;
    if (groups.length === 1) {
        const g = groups[0];
        return !(!g.event_in && g.event_out);
    }
    for (let i = 0; i < groups.length - 1; i++) {
        const g = groups[i];
        if (!g.event_in || !g.event_out) return false;
    }
    return true;
}


const EmployeeStatuses = ({filter, hasScrolled}: {
    filter?: EmployeeStatus,
    hasScrolled?: boolean,
}) => {
    const employeeEvents = useEventsStore(x => x.employeeEvents)
    const navigate = useNavigate()
    const [params] = useSearchParams()

    const {
        atWorkCounter,
        clockedOutCounter,
        notAtWorkCounter,
        wrongDataCounter,
    } = useMemo(() => {
        let atWorkCounter = 0;
        let clockedOutCounter = 0;
        let notAtWorkCounter = 0;
        let wrongDataCounter = 0;

        for (const ee of employeeEvents) {
            const groups = ee.events;

            if (groups.length === 0) {
                notAtWorkCounter += 1;
                continue;
            }

            const valid = isEmployeeEventsValid(groups);
            if (!valid) {
                wrongDataCounter += 1;
                continue;
            }

            const last = groups[groups.length - 1];
            if (last.event_in && last.event_out) {
                clockedOutCounter += 1;
                continue
            }

            const first = groups[0];
            if (first.event_in && !last.event_out) {
                atWorkCounter += 1;
                continue
            }
            if (!first.event_in) {
                wrongDataCounter += 1
            }
        }

        return {
            atWorkCounter,
            clockedOutCounter,
            notAtWorkCounter,
            wrongDataCounter
        };
    }, [employeeEvents]);

    const setFilter = useCallback((value?: EmployeeStatus) => {
        const p = new URLSearchParams(params)
        if (!value) {
            p.delete("employee_status")

        } else {
            p.set("employee_status", value);
        }
        navigate(`?${p.toString()}`, {replace: true})
    }, [params])

    return (
        <FilterTiles compact={hasScrolled}>
            <FilterTile className={styles.green} activeClassName={styles.activeGreen}
                        active={filter === EmployeeStatus.atWork}
                        onClick={() => setFilter(filter === EmployeeStatus.atWork ? undefined : EmployeeStatus.atWork)}>
                {hasScrolled
                    ?
                    <Body_16_Coral_Emphasis_16>
                        {atWorkCounter}
                    </Body_16_Coral_Emphasis_16>
                    :
                    <Title_20_Coral_Strong_emphasis_20>
                        {atWorkCounter}
                    </Title_20_Coral_Strong_emphasis_20>
                }

                <Body_16_Coral_Emphasis_16 style={{textAlign: "center"}}>
                    <Gt prefix={"employee_statuses"}>At work</Gt>
                </Body_16_Coral_Emphasis_16>
            </FilterTile>
            <FilterTile className={styles.red} activeClassName={styles.activeRed}
                        active={filter === EmployeeStatus.notAtWork}
                        onClick={() => setFilter(filter === EmployeeStatus.notAtWork ? undefined : EmployeeStatus.notAtWork)}>
                {hasScrolled
                    ?
                    <Body_16_Coral_Emphasis_16>
                        {notAtWorkCounter}
                    </Body_16_Coral_Emphasis_16>
                    :
                    <Title_20_Coral_Strong_emphasis_20>
                        {notAtWorkCounter}
                    </Title_20_Coral_Strong_emphasis_20>
                }
                <Body_16_Coral_Emphasis_16 style={{textAlign: "center"}}>
                    <Gt prefix={"employee_statuses"}>Not at work</Gt>
                </Body_16_Coral_Emphasis_16>
            </FilterTile>
            <FilterTile className={styles.grey} activeClassName={styles.activeGrey}
                        active={filter === EmployeeStatus.clockedOut}
                        onClick={() => setFilter(filter === EmployeeStatus.clockedOut ? undefined : EmployeeStatus.clockedOut)}>
                {hasScrolled
                    ?
                    <Body_16_Coral_Emphasis_16>
                        {clockedOutCounter}
                    </Body_16_Coral_Emphasis_16>
                    :
                    <Title_20_Coral_Strong_emphasis_20>
                        {clockedOutCounter}
                    </Title_20_Coral_Strong_emphasis_20>
                }
                <Body_16_Coral_Emphasis_16 style={{textAlign: "center"}}>
                    <Gt prefix={"employee_statuses"}>Already clocked out</Gt>
                </Body_16_Coral_Emphasis_16>
            </FilterTile>
            <FilterTile className={styles.mango} activeClassName={styles.activeMango}
                        active={filter === EmployeeStatus.wrongData}
                        onClick={() => setFilter(filter === EmployeeStatus.wrongData ? undefined : EmployeeStatus.wrongData)}>
                {hasScrolled
                    ?
                    <Body_16_Coral_Emphasis_16>
                        {wrongDataCounter}
                    </Body_16_Coral_Emphasis_16>
                    :
                    <Title_20_Coral_Strong_emphasis_20>
                        {wrongDataCounter}
                    </Title_20_Coral_Strong_emphasis_20>
                }
                <Body_16_Coral_Emphasis_16 style={{textAlign: "center"}}>
                    <Gt prefix={"employee_statuses"}>Unable to confirm</Gt>
                </Body_16_Coral_Emphasis_16>
            </FilterTile>
        </FilterTiles>
    )
}

interface Props {
    employee: EmployeeInline,
    events: CheckInOutPair[],
    currentWorkDay?: WorkdayWithEmployee
    createEvent: (kind: KindEnum) => void
    showHistory: () => void
    isPending: boolean
}

const CheckInView = ({employee, events, currentWorkDay, createEvent, isPending, showHistory}: Props) => {
    const gpsState = useGpsStore(x => x.state)
    const gpsBlocked = [GeoState.denied, GeoState.unsupported].includes(gpsState)

    const addEvent = useCallback(async (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        createEvent(KindEnum.In)
    }, [])

    return (
        <Stack className={cn(styles.whiteBoard, styles.employeeItem)} direction={"column"} gap={24}
               onClick={showHistory}>
            <Stack className={styles.employeeInfo} direction={"column"}>
                <Stack justifyContent={"space-between"} style={{width: "100%"}}>
                    <Stack className={styles.employeeName}>
                        <Title_20_Coral_Strong_emphasis_20>
                            {employee.last_name} {employee.first_name}
                        </Title_20_Coral_Strong_emphasis_20>
                    </Stack>
                    <WorkStatus workday={currentWorkDay}/>
                </Stack>
            </Stack>
            {gpsBlocked &&
                <GpsStatusButton ignoreMobileScheme/>
            }

            <Button icon={<LogOut/>} type={ButtonType.secondary}
                    color={ColorNames.CORAL_GREEN}
                    className={styles.startButton}
                    onClick={addEvent}
                    disabled={gpsBlocked || isPending}
            >
                <Gt>Start work</Gt>
            </Button>
        </Stack>
    )
}

const CheckOutView = ({employee, events, currentWorkDay, createEvent, isPending, showHistory}: Props) => {
    const gpsState = useGpsStore(x => x.state)
    const gpsBlocked = [GeoState.denied, GeoState.unsupported].includes(gpsState)
    const checkedDontShowAskModal = useModalState((s) => s.checked);
    const setActiveModal = useModalStore(x => x.setActiveModal);
    const resetModal = useModalStore(x => x.reset);

    const finishAction = useCallback(async (e: any) => {
        e.preventDefault()
        e.stopPropagation()
        if (!currentWorkDay) {
            createEvent(KindEnum.Out)
            return
        }

        if (!checkedDontShowAskModal) {
            setActiveModal(
                <AskFinishWork
                    executeAction={async () => {
                        createEvent(KindEnum.Out)
                        resetModal()
                    }}
                    cancelHandler={resetModal}
                />
            )
            return
        }
        createEvent(KindEnum.Out)
    }, [currentWorkDay])

    return (
        <Stack className={cn(styles.whiteBoard, styles.employeeItem)} direction={"column"} gap={24}
               onClick={showHistory}>
            <Stack className={styles.employeeInfo} direction={"column"}>
                <Stack justifyContent={"space-between"} style={{width: "100%"}}>
                    <Stack className={styles.employeeName}>
                        <Title_20_Coral_Strong_emphasis_20>
                            {employee.last_name} {employee.first_name}
                        </Title_20_Coral_Strong_emphasis_20>
                    </Stack>
                    <WorkStatus workday={currentWorkDay}/>
                </Stack>
            </Stack>
            {gpsBlocked &&
                <GpsStatusButton ignoreMobileScheme/>
            }

            <Button icon={<LogIn color={Colors.Obsidian_Light_0}/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_SKY}
                    className={styles.startButton}
                    onClick={finishAction}
                    disabled={gpsBlocked || isPending}
            >
                <Gt>Finish work</Gt>
            </Button>
        </Stack>
    )
}

type ModalType = "wait" | "success" | "error" | "history" | null

interface EmployeeCardParams {
    employeeEvent: EmployeeEvents,
    filter?: EmployeeStatus,
    workDay?: WorkdayWithEmployee
}

const EmployeeCheckinOutCard = ({employeeEvent, filter, workDay}: EmployeeCardParams) => {
    const employee = employeeEvent.employee
    const events = employeeEvent.events

    const setError = useErrorStore(x => x.setAppError)
    const refetchEvents = useEventsStore(x => x.refetch)

    const [showModal, setShowModal] = useState<ModalType>(null)
    const [preparedPayload, setPreparedPayload] = useState<WorkEventCreateInputRequest | undefined>(undefined)

    const [long, setLong] = useState<number | undefined>(0)
    const [lang, setLang] = useState<number | undefined>(0)
    const [branchName, setBranchName] = useState<string | undefined>(undefined)

    const {mutate, isPending} = useMutation<
        AxiosResponse<WorkEventIngestOutput>,
        AxiosError,
        WorkEventCreateInputRequest
    >({
        mutationFn: async formData => {
            await addEventOffline(formData as WorkEventOutput, employee as EmployeeInline)
            return await apiClient.api.eventsIngest([formData])
        },
        onError: async (e, variables) => {
            await addEventToOutbox(variables?.idempotency_key)
            sleep(1000).then(() => setShowModal("success"))
            return e
        },
        onSuccess: async (result, variables) => {
            if (result?.data?.skipped?.length > 0 || result.data?.created?.length === 0) {
                await addEventToOutbox(variables?.idempotency_key)
                setError({
                    message: "Failed to send data to server",
                    axiosError: {status: 500, message: "Failed to send data to server"} as AxiosError,
                    showModal: true,
                    retry: async () => mutate(variables)
                })
                return
            }
            setBranchName(result?.data?.created?.[0]?.branch_name ?? undefined)
            sleep(1000).then(() => setShowModal("success"))
        },
        networkMode: "always",
    })

    const createEvent = useCallback(async (kind: KindEnum) => {
        setShowModal("wait")
        const payload = {
            kind: kind,
            recorded_at: getCurrentDateTimeIso(),
            longitude: null,
            latitude: null,
            event_employee: employee.id,
            idempotency_key: uuid()
        } as WorkEventCreateInputRequest

        setPreparedPayload(payload)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                useGpsStore.setState({state: GeoState.granted})
                payload.longitude = position.coords.longitude
                payload.latitude = position.coords.latitude
                setLong(position.coords.longitude)
                setLang(position.coords.latitude)

                setPreparedPayload(payload)
                mutate(payload)
            },
            (error) => {
                console.error(error)
                if (error.code === error.PERMISSION_DENIED) useGpsStore.setState({state: GeoState.denied})
                sleep(1000).then(() =>
                    setShowModal("error")
                )
            },
            {enableHighAccuracy: true, timeout: 15000, maximumAge: 0},
        );
    }, [])

    const status = useMemo(() => parseEmployeeStatus(events || []), [events]);
    const kindType = useMemo(() => {
        return [EmployeeStatus.clockedOut, EmployeeStatus.notAtWork].includes(status) ? KindEnum.In : KindEnum.Out
    }, [status])

    if (filter && status !== filter) return null
    return (
        <>
            {showModal === "history" && (
                <Modal onClose={() => {
                    setShowModal(null)
                }}>
                    <EventsHistoryGroup
                        eventsGroup={events}
                        workDay={workDay}
                        employee={employee as EmployeeWithWorkdays}
                    />
                </Modal>
            )}
            {showModal === "wait" && <Modal><GPSWaitModal/></Modal>}
            {showModal === "success" &&
                <Modal onClose={() => {
                    setShowModal(null)
                    if (refetchEvents) refetchEvents()
                }}>
                    <GPSSuccessModal
                        onClose={() => {
                            setShowModal(null)
                            if (refetchEvents) refetchEvents()
                        }}
                        coords={{
                            longitude: long ?? 0,
                            latitude: lang ?? 0,
                        }}
                        placeName={branchName}/>
                </Modal>
            }
            {showModal === "error" &&
                <Modal onClose={() => {
                    setShowModal(null)
                    if (refetchEvents) refetchEvents()
                }}>
                    <GPSFailModal skipAction={() => mutate(preparedPayload as WorkEventCreateInputRequest)}
                                  retryAction={() => createEvent(kindType)}/>
                </Modal>
            }

            {kindType === KindEnum.In
                ? <CheckInView employee={employee} events={events || []} currentWorkDay={workDay}
                               createEvent={createEvent} isPending={isPending}
                               showHistory={() => setShowModal("history")}/>
                : <CheckOutView employee={employee} events={events || []} currentWorkDay={workDay}
                                createEvent={createEvent} isPending={isPending}
                                showHistory={() => setShowModal("history")}/>
            }
        </>
    )
}

export const ManagersCheckInOutPage = () => {
    const searchRef = useRef<InputHandlers>(null)
    const navigate = useNavigate()
    const [params] = useSearchParams()

    const employeeEvents = useEventsStore(x => x.employeeEvents)
    const workdays = useWorkDaysStore(x => x.workdays)
    const [hasScrolled, setHasScrolled] = useState(false);

    const handleScroll = useCallback((el: HTMLDivElement) => {
        const top = el.scrollTop;
        setHasScrolled(prev => (top > 0 ? true : top === 0 ? false : prev));
    }, []);

    const filter = useMemo(() => {
        switch (params.get("employee_status")) {
            case EmployeeStatus.clockedOut:
                return EmployeeStatus.clockedOut
            case EmployeeStatus.notAtWork:
                return EmployeeStatus.notAtWork
            case EmployeeStatus.atWork:
                return EmployeeStatus.atWork
            case EmployeeStatus.wrongData:
                return EmployeeStatus.wrongData
            default:
                return undefined
        }
    }, [params])

    // A list remounted after "no users" starts at the top again
    const listRef = useCallback((node: HTMLDivElement | null) => {
        if (node) handleScroll(node)
    }, [handleScroll]);

    const searchValue = useMemo(() => {
        return params.get("search") ?? ""
    }, [params])

    const searchApply = useCallback((e: string) => {
        const p = new URLSearchParams(params)
        if (!e && e?.length === 0) {
            p.delete("search")
        } else {
            p.set("search", e)
        }
        navigate(`?${p.toString()}`, {replace: true})
    }, [params])

    const usersWorkDays = useMemo(() => {
        const uWd = new Map<number, WorkdayWithEmployee | WorkDay>;
        if (!workdays) return uWd

        for (const day of workdays) {
            const employeeId = "employee" in day ? day?.employee?.id : undefined
            if (!employeeId) continue
            if (!uWd.has(employeeId)) uWd.set(employeeId, day);
        }
        return uWd
    }, [workdays])

    const clear = () => {
        if (!searchRef?.current) return
        const p = new URLSearchParams(params)
        p.delete("search")
        navigate(`?${p.toString()}`, {replace: true})
        searchRef.current.clear()
    }

    return (
        <Stack direction={"column"} gap={16} className={styles.root}>
            <Stack direction={"column"} gap={16}>
                <EmployeeStatuses filter={filter} hasScrolled={hasScrolled}/>
                <Stack className={cn(styles.currentTime, {[styles.timeWhileScroll]: hasScrolled})}
                       justifyContent={"center"}>
                    <Title_40_Coral_emphasis_40>
                        <Clock/>
                    </Title_40_Coral_emphasis_40>
                </Stack>
            </Stack>
            <Stack className={cn(styles.content, styles.searchBox, {[styles.hide]: hasScrolled})}>
                <Input
                    grow
                    ref={searchRef}
                    leftIcon={<Search size={16} color={Colors.Obsidian_Dark_50}/>}
                    rightIcon={searchValue ? <ClearResultsButton clickEvent={clear}/> : null}
                    name={'search'}
                    placeholder={t('Search')}
                    className={styles.searchInput}
                    initialValue={searchValue}
                    onKeyDown={e => {
                        if (e.key === 'Enter') { // @ts-ignore
                            searchApply(e?.target?.value || "")
                        }
                    }}
                />
            </Stack>

            {(employeeEvents.length > 0)
                ?
                <div className={styles.employeeList} ref={listRef} onScroll={e => handleScroll(e.currentTarget)}>
                    <Stack className={cn(styles.gradiented, {[styles.hide]: !hasScrolled})}/>
                    {employeeEvents.map(ee =>
                        <EmployeeCheckinOutCard
                            employeeEvent={ee}
                            key={ee.employee.id}
                            filter={filter}
                            workDay={usersWorkDays.get(ee.employee.id) as WorkdayWithEmployee}
                        />
                    )}
                </div>
                : <DataNotFoundMessage text={<Gt>We couldn’t find users</Gt>}/>
            }
        </Stack>
    )
}


export const ManagerView = () => {
    const isEventsLoading = useEventsStore(x => x.isLoading)
    if (isEventsLoading) return <CheckInOutSkeleton/>

    return (
        <Stack grow direction={"column"} alignItems={"center"}
               justifyContent={"flex-start"} gap={16}>
            <Stack direction={"column"} className={styles.header} gap={0}>
                <Header showFilters={true}/>
                <StatusBanner/>
            </Stack>

            <Stack grow className={styles.content}>
                <ManagersCheckInOutPage/>
            </Stack>
        </Stack>
    )
}
